import React from "react";
import { Link } from "react-router-dom";
import {
  Thermometer,
  Bug,
  PackageCheck,
  TriangleAlert,
  GraduationCap,
  SprayCan,
} from "lucide-react";
import useSWR from "swr";
import Header from "@/components/Header";
import { getToday } from "@/lib/api";

const RECORDS = [
  {
    slug: "temperature",
    title: "Temperatures",
    sub: "Vitrina, neveres",
    freq: "Diari",
    Icon: Thermometer,
    accent: "bg-terracotta text-white",
    daily: true,
  },
  {
    slug: "pest",
    title: "Plagues",
    sub: "Inspecció visual",
    freq: "Diari",
    Icon: Bug,
    accent: "bg-forest text-white",
    daily: true,
  },
  {
    slug: "cleaning",
    title: "Neteja",
    sub: "Neteja i desinfecció",
    freq: "Segons pla",
    Icon: SprayCan,
    accent: "bg-forest text-white",
    daily: false,
  },
  {
    slug: "reception",
    title: "Recepció",
    sub: "Mercaderies entrades",
    freq: "Per entrada",
    Icon: PackageCheck,
    accent: "bg-ink text-white",
    daily: false,
  },
  {
    slug: "incidence",
    title: "Incidències",
    sub: "I mesures correctores",
    freq: "Quan calgui",
    Icon: TriangleAlert,
    accent: "bg-ember text-white",
    daily: false,
  },
  {
    slug: "training",
    title: "Formació",
    sub: "Activitats formatives",
    freq: "Quan calgui",
    Icon: GraduationCap,
    accent: "bg-cream text-ink",
    daily: false,
  },
];

export default function FillSelector() {
  return (
    <>
      <Header title="Omplir registre" />
      <div className="mx-auto max-w-3xl px-4 pb-24 pt-6 sm:px-6 sm:pt-10">
        <div className="animate-fade-up">
          <div className="label-eyebrow">Quin registre vols omplir?</div>
          <h2 className="mt-2 font-display text-3xl font-bold leading-tight text-ink sm:text-4xl">
            Tria una opció
          </h2>
          <p className="mt-2 text-sm text-ink-soft">
            Els registres marcats com <strong className="text-terracotta">diaris</strong> s'han d'omplir cada dia.
          </p>
        </div>

        <DailyAlert />

        <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {RECORDS.map((r, idx) => (
            <Link
              key={r.slug}
              to={`/fill/${r.slug}`}
              data-testid={`record-card-${r.slug}`}
              className="group flex items-center gap-4 rounded-2xl border border-line bg-white p-5 shadow-card transition-all animate-fade-up hover:-translate-y-0.5 hover:shadow-soft"
              style={{ animationDelay: `${idx * 40}ms` }}
            >
              <div
                className={
                  "flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl " +
                  r.accent
                }
              >
                <r.Icon size={24} />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-display text-xl font-bold text-ink">
                    {r.title}
                  </h3>
                  {r.daily && (
                    <span className="rounded-full bg-terracotta/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-terracotta">
                      Diari
                    </span>
                  )}
                </div>
                <div className="mt-0.5 text-sm text-ink-soft">{r.sub}</div>
                <div className="mt-1 text-xs text-ink-soft/70">{r.freq}</div>
              </div>
              <div className="text-ink-soft transition-transform group-hover:translate-x-1">
                →
              </div>
            </Link>
          ))}
        </div>
      </div>
    </>
  );
}

function DailyAlert() {
  const { data } = useSWR("today", getToday);
  if (!data) return null;
  const pending = [];
  if (!data.temperature_done) pending.push("Temperatures");
  if (!data.pest_done) pending.push("Plagues");
  if (pending.length === 0) {
    return (
      <div
        data-testid="daily-alert-complete"
        className="mt-6 flex items-start gap-3 rounded-2xl border border-forest/20 bg-forest-bg p-4"
      >
        <div className="mt-0.5 text-forest">✓</div>
        <div className="text-sm leading-relaxed text-forest">
          <strong>Avui ja està.</strong> Els registres diaris (temperatures i
          plagues) ja s'han omplert.
        </div>
      </div>
    );
  }
  return (
    <div
      data-testid="daily-alert-pending"
      className="mt-6 flex items-start gap-3 rounded-2xl border border-terracotta/20 bg-terracotta/5 p-4"
    >
      <div className="mt-0.5 text-terracotta">!</div>
      <div className="text-sm leading-relaxed text-ink">
        <strong className="text-terracotta">Avui falta</strong>: omplir{" "}
        {pending.join(" i ")}.
      </div>
    </div>
  );
}
